<form #newModule="ngForm" (ngSubmit)="createupdatemodule()" class="course-form mb-0">

  <!-- Page header -->
  <header class="cf-head">
    <span class="cf-head__icon"><i class="fas fa-graduation-cap" aria-hidden="true"></i></span>
    <div class="cf-head__text">
      <h1 class="cf-head__title">{{moduleid ? 'Edit Course' : 'Create New Course'}}</h1>
      <p class="cf-head__sub">Add course details and configure settings</p>
    </div>
    <div class="cf-head__actions">
      <button class="btn btn--warning m-0" type="button" *ngIf="!moduleid && schoolid" routerLink="/user/schoolhome">Cancel</button>
      <button class="btn btn--warning m-0" type="button" *ngIf="moduleid" routerLink="/user/moduledetails"
        [queryParams]="{moduleID: module.id}">Cancel</button>
      <button class="btn m-0" [disabled]="!newModule.form.valid" type="submit">
        <i class="fas fa-save" aria-hidden="true"></i> Save Course
      </button>
    </div>
  </header>

  <!-- Header image -->
  <section class="cf-card">
    <div class="cf-card__split">
      <div>
        <h2 class="cf-card__title">Header Image</h2>
        <p class="cf-card__hint">Add a header image to make your course stand out</p>
      </div>
      <div class="cf-image">
        <div *ngIf="logoimg.style.display === 'none'" class="cf-image__empty">
          <i class="far fa-image" aria-hidden="true"></i>
          <span>No image added</span>
          <small>Recommended {{settings.moduleImageWidth}} x {{settings.moduleImageHeight}}px</small>
        </div>
        <img #logoimg src="{{module.logo}}" alt="Course header image" class="cf-image__preview" style="display: none;">
        <button (click)="logo.click()" type="button" id="courseHeaderImgLabel" class="btn m-0 mt-2">
          <i class="fa fa-plus"></i> Add Header Image
        </button>
        <input #logo (change)="handleLogoFileChange($event.target.files)" type="file" name="logo" accept="image/*"
          aria-labelledby="courseHeaderImgLabel" id="courseHeaderImg" class="sr-only">
      </div>
    </div>
  </section>

  <!-- Basic information -->
  <section class="cf-card">
    <h2 class="cf-card__title"><i class="far fa-file-alt" aria-hidden="true"></i> Basic Information</h2>

    <div class="cf-grid">
      <div class="fieldset fieldset--lg">
        <label class="legend" for="schoolname">Course Name <span class="requiredfield">*</span></label>
        <input type="text" [(ngModel)]="module.name" #name name="name" maxlength="200" required class="text-field"
          id="schoolname">
      </div>
      <div class="fieldset fieldset--lg">
        <label class="legend" for="code">Course Code</label>
        <input type="text" [(ngModel)]="module.courseCode" #courseCode name="courseCode" class="text-field" id="code">
      </div>
    </div>

    <div class="cf-description">
      <div class="fieldset fieldset--note">
        <label class="legend z-1">Course Description <span class="requiredfield">*</span></label>
        <!-- Bound with ngModel so `required` actually applies. It previously carried a bare
             `required` attribute, which does nothing on a custom component — a course could
             be saved with no description at all despite the asterisk. -->
        <quill-editor #description name="description" required [(ngModel)]="module.description"
          placeholder="Describe what this course is about..."></quill-editor>
      </div>
      <aside class="cf-tips">
        <h3><i class="far fa-lightbulb" aria-hidden="true"></i> Tips for a great description</h3>
        <ul>
          <li>Explain what students will learn</li>
          <li>Highlight key benefits</li>
          <li>Keep it clear and engaging</li>
          <li>Use short paragraphs</li>
        </ul>
      </aside>
    </div>
  </section>

  <!-- Course settings -->
  <section class="cf-card">
    <h2 class="cf-card__title"><i class="fas fa-cog" aria-hidden="true"></i> Course Settings</h2>

    <div class="cf-grid cf-grid--three">
      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="allowBatches">Batch Students</label>
        <select class="text-field" [(ngModel)]="module.allowBatches" #allowBatches name="allowBatches" id="allowBatches">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
        <button type="button" data-toggle="dropdown" aria-expanded="false" title="About batches"
          class="position-absolute right-0 top-0 bottom-0 m-auto d-flex align-items-center pr-3 bg-transparent border-0 text-body"><i
            class="fa fa-info-circle"></i></button>
        <div class="info-popup dropdown-menu p-2">
          <p class="form-info-p">Teach your course in batches to make sure all your students start and end the course together. Extremely useful when you intend to teach your students in live classes</p>
        </div>
      </div>

      <div class="fieldset fieldset--lg">
        <!-- id was "accessDays" — a duplicate of the field below — and this also flagged an
             access-days change, so editing the pass mark rewrote every existing student's
             access period. -->
        <label class="legend" for="passScore">Pass Score (%)</label>
        <input type="number" [(ngModel)]="module.passScorePercentage" #passScorePercentage name="passScorePercentage"
          min="0" max="100" class="text-field" id="passScore">
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend" for="price">Price</label>
        <input type="number" [(ngModel)]="module.price" #price name="price" min="0" class="text-field" id="price">
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend" for="accessDays">Access Days</label>
        <input type="number" [(ngModel)]="module.accessdays" #accessdays name="accessdays" min="0" class="text-field"
          id="accessDays" (change)="this.accessDaysChanged = true;">
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="reqEnroll">Require Enrollment</label>
        <select class="text-field" [(ngModel)]="module.requireEnrollment" #requireEnrollment name="requireEnrollment"
          id="reqEnroll" [disabled]="module.price">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
        <small class="cf-note" *ngIf="module.price">Always required for a paid course</small>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="retakemultiplechoice">Allow Retake for Multiple Choice Assessment</label>
        <select class="text-field" [(ngModel)]="module.retakemultiplechoice" #retakemultiplechoice
          name="retakemultiplechoice" id="retakemultiplechoice">
          <option [ngValue]="true">Yes</option>
          <option [ngValue]="false" selected>No</option>
        </select>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="userCompleteCourseWhenAllLessonsAreCompleted">Complete When All Lessons Done</label>
        <select class="text-field" [(ngModel)]="module.userCompleteCourseWhenAllLessonsAreCompleted"
          #userCompleteCourseWhenAllLessonsAreCompleted name="userCompleteCourseWhenAllLessonsAreCompleted"
          id="userCompleteCourseWhenAllLessonsAreCompleted">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="sendMarkedNotification">Send Notification on Grade</label>
        <select class="text-field" [(ngModel)]="module.sendMarkedNotification" #sendMarkedNotification
          name="sendMarkedNotification" id="sendMarkedNotification">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="sendMilestoneNotification">Activate Lesson Deadline</label>
        <select class="text-field" [(ngModel)]="module.activateLessonDeadlineNotification" #sendMilestoneNotification
          name="sendMilestoneNotification" id="sendMilestoneNotification">
          <option [ngValue]="true">Yes</option>
          <option [ngValue]="false" selected>No</option>
        </select>
        <button type="button" data-toggle="dropdown" aria-expanded="false" title="About lesson deadlines"
          class="position-absolute right-0 top-0 bottom-0 m-auto d-flex align-items-center pr-3 bg-transparent border-0 text-body"><i
            class="fa fa-info-circle"></i></button>
        <div class="info-popup dropdown-menu p-2">
          <p class="form-info-p">Gives each lesson a due date and emails students a reminder as it approaches.
            The due date is worked out from the deadline you set on the lesson itself, counted from the day
            the student enrolled or from the lesson's publish date if it uses a fixed deadline. Students
            see the dates on their progress timeline, and anything overdue is marked. Lessons with no
            deadline set are unaffected.</p>
        </div>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="reqCompletion">Require Lesson Completion in Order</label>
        <select class="text-field" [(ngModel)]="module.requireLessonCompletion" #requireLessonCompletion
          name="requireLessonCompletion" id="reqCompletion">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="sendCertificateOnCompletion">Generate Certificate On Completion</label>
        <select class="text-field" [(ngModel)]="module.sendCertificateOnCompletion" #sendCertificateOnCompletion
          name="sendCertificateOnCompletion" id="sendCertificateOnCompletion">
          <option [ngValue]="true" selected>Yes</option>
          <option [ngValue]="false">No</option>
        </select>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend" for="publishDate">Publish Date</label>
        <input [owlDateTime]="rawpublisheddatetime" class="text-field" id="publishDate" name="publishDate"
          #publishDate [(ngModel)]="module.publishDate">
        <button type="button" [owlDateTimeTrigger]="rawpublisheddatetime"
          class="owlDateTimeTrigger position-absolute right-4 top-0 bottom-0 m-auto d-flex align-items-center px-3 bg-transparent border-0 text-body"
          title="Calendar"><i class="fa fa-calendar"></i></button>
        <owl-date-time #rawpublisheddatetime></owl-date-time>
        <button type="button" data-toggle="dropdown" aria-expanded="false" title="Date of availability"
          class="position-absolute right-0 top-0 bottom-0 m-auto d-flex align-items-center pr-3 bg-transparent border-0 text-body"><i
            class="fa fa-info-circle"></i></button>
        <div class="info-popup dropdown-menu p-2">
          <p class="form-info-p">This is the date from which the course will be made available</p>
        </div>
      </div>

      <div class="fieldset fieldset--lg">
        <label class="legend z-1" for="virtualInstructor">Virtual Instructor-Led</label>
        <select class="text-field" [(ngModel)]="module.virtualInstructor" #virtualInstructor name="virtualInstructor"
          id="virtualInstructor">
          <option [ngValue]="true">Yes</option>
          <option [ngValue]="false" selected>No</option>
        </select>
      </div>

      <div *ngIf="module.virtualInstructor" class="fieldset fieldset--lg">
        <label class="legend" for="virtualschedule">Virtual Meeting Time Schedule</label>
        <input type="text" [(ngModel)]="module.virtualSchedule" #virtualschedule name="virtualschedule" maxlength="200"
          class="text-field" id="virtualschedule">
        <button type="button" data-toggle="dropdown" aria-expanded="false" title="Meeting schedule"
          class="position-absolute right-0 top-0 bottom-0 m-auto d-flex align-items-center pr-3 bg-transparent border-0 text-body"><i
            class="fa fa-info-circle"></i></button>
        <div class="info-popup dropdown-menu p-2">
          <p class="form-info-p">Recurring schedule for virtual meetings (e.g., Mondays, 6:00 PM - 7:30 PM GMT).</p>
        </div>
      </div>

      <div *ngIf="module.virtualInstructor" class="fieldset fieldset--lg">
        <label class="legend z-1" for="startDate">Start Date</label>
        <input [owlDateTime]="startdate" class="text-field" id="startDate" name="startDate" #startDate
          [(ngModel)]="module.startDate">
        <button type="button" [owlDateTimeTrigger]="startdate"
          class="owlDateTimeTrigger position-absolute right-4 top-0 bottom-0 m-auto d-flex align-items-center px-3 bg-transparent border-0 text-body"
          title="Calendar"><i class="fa fa-calendar"></i></button>
        <owl-date-time #startdate></owl-date-time>
        <button type="button" data-toggle="dropdown" aria-expanded="false" title="Start Date"
          class="position-absolute right-0 top-0 bottom-0 m-auto d-flex align-items-center pr-3 bg-transparent border-0 text-body"><i
            class="fa fa-info-circle"></i></button>
        <div class="info-popup dropdown-menu p-2">
          <p class="form-info-p">This is the date the course starts, will be displayed in course calendar</p>
        </div>
      </div>
    </div>
  </section>

  <!-- Footer -->
  <div class="cf-footer">
    <span class="cf-footer__hint">
      <i class="fas fa-graduation-cap" aria-hidden="true"></i>
      <span>
        <strong>After saving, you'll be able to add lessons, quizzes and more.</strong><br>
        You can always edit some of these settings later.
      </span>
    </span>
    <span class="cf-footer__actions">
      <button class="btn btn--warning m-0" type="button" *ngIf="!moduleid && schoolid" routerLink="/user/schoolhome">Cancel</button>
      <button class="btn btn--warning m-0" type="button" *ngIf="moduleid" routerLink="/user/moduledetails"
        [queryParams]="{moduleID: module.id}">Cancel</button>
      <button class="btn m-0" [disabled]="!newModule.form.valid" type="submit">Save &amp; Continue</button>
    </span>
  </div>
</form>

<div *ngIf="this.moduleid" class="modal fade" #mediaModal tabindex="-1" role="dialog" aria-labelledby="mediaModalLabel" aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title" id="mediaModalLabel">Select Media</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <app-module-media-library [moduleid]="this.moduleid" [enableselect]="true"
          (mediaSelected)="mediaSelected($event)"></app-module-media-library>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn--warning" data-dismiss="modal">Close</button>
      </div>
    </div>
  </div>
</div>
<div class="modal fade" #externalMediaModal tabindex="-1" role="dialog" aria-labelledby="externalMediaModalLabel"
  aria-hidden="true">
  <div class="modal-dialog modal-dialog-centered" role="document">
    <div class="modal-content">
      <div class="modal-header">
        <h3 class="modal-title" id="externalMediaModalLabel">Select Media</h3>
        <button type="button" class="close" data-dismiss="modal" aria-label="Close">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
      <div class="modal-body">
        <app-add-external-media #addExternalMediaComponent></app-add-external-media>
      </div>      
      <div class="modal-footer justify-content-center gap-1.5">
        <button type="button" class="btn m-0" (click)="externalMediaSelected()"
          [disabled]="!addExternalMediaComponent.iframeurl">Ok</button>
        <button type="button" class="btn btn--warning m-0" (click)="closeExternalMediaModal()">Close</button>
      </div>
    </div>
  </div>
</div>
